import { BREAKPOINTS, DSFlex, DSSpacer, DSText } from '@zoe/ds-web'
import React from 'react'
import styled from 'styled-components'

import { FragmentComponent } from '@/types/graphql'

import { responsiveImageBlock } from '@/graphql/utils/commonBlocks'

import { DSGridBlock } from '@/components/ds/Grid/Grid'

import { trackCtaClick } from '@/lib/gtm/gtmV2'

import { processTypographyMarkdown } from '../DesignSystem/Typography'

export type PodcastBlockRecord = {
  __typename: 'PodcastBlockRecord'
  id: string
  eyebrow?: string
  title?: string
  label?: string
  dataTestId?: string
  backgroundImage?: {
    responsiveImage: {
      src: string
      alt?: string
    }
  }
  smallBackgroundImage?: {
    responsiveImage: {
      src: string
      alt?: string
    }
  }
  image?: {
    responsiveImage: {
      src: string
      alt: string
    }
  }
}

const fragment = `
  fragment PodcastBlockFragment on PodcastSectionRecord {
    __typename
    id
    eyebrow
    title
    label
    image {
      ${responsiveImageBlock({ width: 600, height: 700 })}
    }
    backgroundImage {
      ${responsiveImageBlock({ width: 2880, height: 1237 })}
    }
    smallBackgroundImage {
      ${responsiveImageBlock({ width: 400, height: 800 })}
    }
    dataTestId
  }
`

const StyledContainer = styled.div<{ $bgImageSrc?: string; $smallBgImageSrc?: string }>`
  position: relative;
  min-height: 780px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: var(--color-surface-yellow-bold);
  ${({ $bgImageSrc }) =>
    $bgImageSrc &&
    `
    background-image: url(${$bgImageSrc});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  `}
  @media (max-width: ${BREAKPOINTS.m}) {
    ${({ $smallBgImageSrc }) =>
      $smallBgImageSrc &&
      `
      background-image: url(${$smallBgImageSrc});
    `}
  }
`

const StyledPodcastButtonsContainer = styled.div`
  background-color: hsla(0, 0%, 100%, 0.2);
  border: 1px solid hsla(0, 0%, 100%, 0.21);
  border-radius: var(--radius-full);
  float: left;
`

const StyledImageContainer = styled.div`
  max-width: 600px;
  height: 100%;
  overflow: hidden;
`

const StyledLabelContainer = styled.div`
  @media (max-width: ${BREAKPOINTS.m}) {
    display: none;
  }
`

const StyledPodcastContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  height: 100%;
  @media (max-width: ${BREAKPOINTS.m}) {
    align-items: center;
  }
`
const trackPodcastPlatformClick = (platform: string) => {
  trackCtaClick('homepage', `listen_on_${platform}`)
}

export const PodcastBlock: FragmentComponent<{}, PodcastBlockRecord> = ({ record }) => {
  return (
    <StyledContainer
      $bgImageSrc={record.backgroundImage?.responsiveImage.src}
      $smallBgImageSrc={record.smallBackgroundImage?.responsiveImage.src}
    >
      <DSSpacer direction="vertical" size={72} />
      <DSFlex
        breakConfig={{
          maxViewportDirection: 'row',
          breakAt: {
            breakPoint: 'm',
            direction: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 48,
          },
        }}
        justifyContent="center"
        wrap="wrap"
      >
        <DSGridBlock
          dataTestId={record?.dataTestId}
          padded
          breakConfig={{
            s: { colSpan: 2, order: 2 },
            m: { colSpan: 6, order: 2 },
            l: { colSpan: 4, offset: 1 },
            xl: { colSpan: 3, offset: 1 },
          }}
        >
          <StyledPodcastContent>
            {record.eyebrow && (
              <DSText
                as="h5"
                variant="fluid-heading-500alt"
                weight="semiBold"
                align="left"
                breakAt={{ breakPoint: 'm', align: 'center' }}
              >
                {record.eyebrow}
              </DSText>
            )}
            <DSSpacer direction="vertical" size={16} />
            {record.title && (
              <DSText
                as="h2"
                variant="fluid-heading-900"
                align="left"
                breakAt={{ breakPoint: 'm', variant: 'fluid-heading-800', align: 'center' }}
              >
                {processTypographyMarkdown({ content: record.title, variant: 'fluid-heading-900' })}
              </DSText>
            )}
            <DSSpacer direction="vertical" size={32} />
            <StyledPodcastButtonsContainer>
              <DSSpacer direction="vertical" size={24} breakAt={{ m: 16 }} />
              <DSFlex direction="row" alignItems="center">
                <DSSpacer direction="horizontal" size={32} breakAt={{ m: 24 }} />
                {record.label && (
                  <StyledLabelContainer>
                    <DSText as="span" variant="fluid-paragraph-200" align="left">
                      {record.label}
                    </DSText>
                    <DSSpacer direction="horizontal" size={32} breakAt={{ m: 24 }} />
                  </StyledLabelContainer>
                )}
                <a
                  href="https://open.spotify.com/show/1xDawqT8Cgm6HAnbpFX5T5?si=gMKxn5qWTq6MFEz4doL46A"
                  target="_blank"
                  onClick={() => trackPodcastPlatformClick('spotify')}
                  rel="noopener noreferrer"
                >
                  <img src="assets/img/podcast/new/spotify.svg" alt="Listen on Spotify" height={32} />
                </a>
                <DSSpacer direction="horizontal" size={32} breakAt={{ m: 24 }} />
                <a
                  href="https://youtube.com/@joinzoe?si=1bDeMw5VCyHkXRPxsub_confirmation=1"
                  target="_blank"
                  onClick={() => trackPodcastPlatformClick('youtube')}
                >
                  <img src="assets/img/podcast/new/yt.svg" alt="Listen on YouTube" height={32} />
                </a>
                <DSSpacer direction="horizontal" size={32} breakAt={{ m: 24 }} />
                <a
                  href="https://music.amazon.co.uk/podcasts/c3d23565-3f36-47fc-97d6-044a251b57bf/zoe-science-nutrition?ref=dm_sh_BYjXqPztz2XLMQrndk7Mye0Ck"
                  target="_blank"
                  onClick={() => trackPodcastPlatformClick('amazon')}
                  rel="noopener noreferrer"
                >
                  <img src="assets/img/podcast/new/amazon-music.svg" alt="Listen on Amazon Music" height={32} />
                </a>
                <DSSpacer direction="horizontal" size={32} breakAt={{ m: 24 }} />
                <a
                  href="https://podcasts.apple.com/gb/podcast/zoe-science-nutrition/id1611216298"
                  target="_blank"
                  onClick={() => trackPodcastPlatformClick('apple')}
                >
                  <img src="assets/img/podcast/new/ios-podcast.svg" alt="Listen on Apple Podcasts" height={32} />
                </a>
                <DSSpacer direction="horizontal" size={32} breakAt={{ m: 24 }} />
              </DSFlex>

              <DSSpacer direction="vertical" size={24} breakAt={{ m: 16 }} />
            </StyledPodcastButtonsContainer>
          </StyledPodcastContent>
        </DSGridBlock>
        {record.image && (
          <StyledImageContainer>
            <img
              src={record.image.responsiveImage.src}
              alt={record.image.responsiveImage.alt}
              style={{ width: '100%', height: 'auto', borderRadius: 32 }}
            />
          </StyledImageContainer>
        )}
      </DSFlex>
      <DSSpacer direction="vertical" size={72} />
    </StyledContainer>
  )
}

PodcastBlock.fragment = fragment
PodcastBlock.recordName = 'PodcastBlockRecord'
